/** Field labels for sales order editor dirty hover (matches serializeSalesOrderEditorSnapshot). */

type EditorItemSnapshot = {
  sku: string;
  productDescription: string;
  quantity: string;
  unitPrice: string;
  remark: string;
  origin: string;
};

type EditorSnapshot = {
  shipByDate: string;
  customerPurchaseOrderNumber: string;
  paymentTerms: string;
  clientFsAccountId: string | null;
  endUserFsAccountId: string | null;
  shipToRecipient: string;
  shipToAttention: string;
  shipToAddressLine1: string;
  shipToAddressLine2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  billToRecipient: string;
  billToLine1: string;
  billToLine2: string;
  billToLine3: string;
  billToCity: string;
  billToState: string;
  billToZip: string;
  billToCountry: string;
  pilotProgram: boolean;
  specialInstructions: string;
  shippingChargeMode: string;
  magnetChargeMode: string;
  trackingRecipientEmails: string[];
  invoiceRecipientEmails: string[];
  items: EditorItemSnapshot[];
  attachments: string[];
};

const SCALAR_FIELD_LABELS: Array<{ key: keyof EditorSnapshot; label: string }> = [
  { key: "shipByDate", label: "Ship by date" },
  { key: "customerPurchaseOrderNumber", label: "Customer purchase order" },
  { key: "paymentTerms", label: "Payment terms" },
  { key: "clientFsAccountId", label: "Client" },
  { key: "endUserFsAccountId", label: "End user" },
  { key: "shipToRecipient", label: "Ship-to recipient" },
  { key: "shipToAttention", label: "Ship-to attention" },
  { key: "shipToAddressLine1", label: "Ship-to address line 1" },
  { key: "shipToAddressLine2", label: "Ship-to address line 2" },
  { key: "shipToCity", label: "Ship-to city" },
  { key: "shipToState", label: "Ship-to state" },
  { key: "shipToZip", label: "Ship-to ZIP" },
  { key: "shipToCountry", label: "Ship-to country" },
  { key: "billToRecipient", label: "Bill-to recipient" },
  { key: "billToLine1", label: "Bill-to address line 1" },
  { key: "billToLine2", label: "Bill-to address line 2" },
  { key: "billToLine3", label: "Bill-to address line 3" },
  { key: "billToCity", label: "Bill-to city" },
  { key: "billToState", label: "Bill-to state" },
  { key: "billToZip", label: "Bill-to ZIP" },
  { key: "billToCountry", label: "Bill-to country" },
  { key: "pilotProgram", label: "Pilot program" },
  { key: "specialInstructions", label: "Customer-facing note" },
  { key: "shippingChargeMode", label: "Shipping charge mode" },
  { key: "magnetChargeMode", label: "Magnet charge mode" },
];

function parseEditorSnapshot(serialized: string): EditorSnapshot | null {
  try {
    const parsed = JSON.parse(serialized) as EditorSnapshot;
    if (parsed == null || typeof parsed !== "object") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function sameStringList(left: string[] | undefined, right: string[] | undefined): boolean {
  const a = left ?? [];
  const b = right ?? [];
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
}

function sameItems(left: EditorItemSnapshot[] | undefined, right: EditorItemSnapshot[] | undefined): boolean {
  const a = left ?? [];
  const b = right ?? [];
  if (a.length !== b.length) {
    return false;
  }
  return a.every((item, index) => {
    const other = b[index];
    return (
      item.sku === other.sku &&
      item.productDescription === other.productDescription &&
      item.quantity === other.quantity &&
      item.unitPrice === other.unitPrice &&
      item.remark === other.remark &&
      item.origin === other.origin
    );
  });
}

/**
 * Human labels for fields that differ between the captured baseline and the live editor snapshot.
 * Returns an empty array when snapshots match or cannot be parsed.
 */
export function listSalesOrderEditorDirtyFields(baseline: string | null, snapshot: string): string[] {
  if (baseline == null || baseline === snapshot) {
    return [];
  }
  const before = parseEditorSnapshot(baseline);
  const after = parseEditorSnapshot(snapshot);
  if (before == null || after == null) {
    return ["Order form"];
  }

  const labels: string[] = [];
  for (const field of SCALAR_FIELD_LABELS) {
    if (before[field.key] !== after[field.key]) {
      labels.push(field.label);
    }
  }
  if (!sameStringList(before.trackingRecipientEmails, after.trackingRecipientEmails)) {
    labels.push("Tracking recipients");
  }
  if (!sameStringList(before.invoiceRecipientEmails, after.invoiceRecipientEmails)) {
    labels.push("Invoice recipients");
  }
  if (!sameItems(before.items, after.items)) {
    labels.push("Line items");
  }
  if (!sameStringList(before.attachments, after.attachments)) {
    labels.push("Attachments");
  }
  return labels.length > 0 ? labels : ["Order form"];
}

/**
 * True when the editor party pickers match the persisted order ids (including both null).
 * Baseline capture must wait for this so client/end user are not snapshotted as null first.
 */
export function salesOrderEditorPartyBaselineReady(
  expectedClientFsAccountId: string | null | undefined,
  expectedEndUserFsAccountId: string | null | undefined,
  clientFsAccountId: string | null | undefined,
  endUserFsAccountId: string | null | undefined,
): boolean {
  return (
    (expectedClientFsAccountId ?? null) === (clientFsAccountId ?? null) &&
    (expectedEndUserFsAccountId ?? null) === (endUserFsAccountId ?? null)
  );
}

/** Plain-text hover body for HubSaveButton dirtyHint. */
export function formatSalesOrderEditorDirtyHint(fields: string[]): string | null {
  if (fields.length === 0) {
    return null;
  }
  return `Unsaved changes:\n${fields.map((field) => `- ${field}`).join("\n")}`;
}
